<template>
    <div>
        <template v-if="!isEmbed">
            <div class="mb-3 text-2xl font-semibold tracking-wider">
                {{ catalog.title }}
            </div>
            <div v-html="catalog.body" />
        </template>

        <div class="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <template
                v-for="child in children"
                :key="child.child.slug"
            >
                <nuxt-link
                    v-if="child.intercept !== undefined"
                    custom
                    :to="child.route"
                >
                    <a
                        :href="child.route"
                        @click.prevent="handleClick($event, child.intercept)"
                    >
                        <storefront-catalog-child :child="child.child" />
                    </a>
                </nuxt-link>

                <nuxt-link
                    v-else
                    :to="child.route"
                    :target="child.target"
                >
                    <storefront-catalog-child :child="child.child" />
                </nuxt-link>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { StorefrontCatalogScalar } from '~/resources/scalars/storefront-catalog-scalar'
import { computed } from 'vue'
import NuxtLink from '#app/components/nuxt-link'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useHead } from 'unhead'
import {
    layoutModeKioskLink,
    layoutModeLightboxLink,
    unsafe_facilityCreateContractLink,
    useRouteLink
} from '~/composables/routing'
import { useNuxtApp } from '#app'
import { useEmbedParams } from '~/composables/use-embed-params'
import StorefrontCatalogChild from '~/components/Storefront/StorefrontCatalogChild.vue'
import { getRequestHost } from 'h3'
import type { PlanType } from '~/resources/graphql/graphql'
import { useLayoutMode } from '~/composables/use-layout-mode'

const props = withDefaults(
    defineProps<{
        catalog: StorefrontCatalogScalar
        storefrontPrefix: string
        root?: boolean
    }>(),
    {
        root: false
    }
)

// Only render title we are the root catalog
if (props.root) {
    const activeFacility = useActiveFacility()
    useHead({
        title: activeFacility.value.title
    })
}

const nuxt = useNuxtApp()
const locationOrigin = computed(() => {
    if (process.server && nuxt.ssrContext) {
        return getRequestHost(nuxt.ssrContext.event)
    } else if (location !== undefined) {
        return location.origin
    } else {
        return ''
    }
})

const embedParams = useEmbedParams()
const { kioskSlug } = useLayoutMode()
function makeContractLink(planType: PlanType, slug: string): string {
    if (kioskSlug.value !== null) {
        return layoutModeKioskLink(
            kioskSlug.value,
            unsafe_facilityCreateContractLink(
                props.storefrontPrefix,
                planType,
                slug
            )
        )
    } else {
        return facilityCreateContractLink(
            props.storefrontPrefix,
            planType,
            slug
        )
    }
}

const activeFacility = useActiveFacility()
const { storefrontNodeLink, facilityCreateContractLink } = useRouteLink()
const isEmbed = embedParams !== null
const children = computed(() => {
    return props.catalog.children.map(child => {
        if (child.__typename === 'StorefrontPlan') {
            return {
                child,
                target:
                    embedParams !== null && embedParams.mode === 'new-tab'
                        ? '_blank'
                        : undefined,
                route: makeContractLink(child.planType, child.slug),
                intercept:
                    embedParams !== null && embedParams.mode === 'same-tab'
                        ? {
                              key: 'parent-redirect',
                              url:
                                  locationOrigin.value +
                                  unsafe_facilityCreateContractLink(
                                      props.storefrontPrefix,
                                      child.planType,
                                      child.slug
                                  )
                          }
                        : embedParams !== null &&
                          embedParams.mode === 'lightbox'
                        ? {
                              key: 'lightbox',
                              backgroundColor:
                                  activeFacility.value.branding.primaryColor,
                              url:
                                  locationOrigin.value +
                                  layoutModeLightboxLink(
                                      unsafe_facilityCreateContractLink(
                                          props.storefrontPrefix,
                                          child.planType,
                                          child.slug
                                      )
                                  )
                          }
                        : undefined
            }
        }

        return {
            child,
            target: undefined,
            route: isEmbed
                ? `${props.storefrontPrefix}/embed/${child.slug}`
                : storefrontNodeLink(props.storefrontPrefix, child.slug)
        }
    })
})

function handleClick(event: PointerEvent, message: Record<string, string>) {
    parent.postMessage('RPHQ::1::' + JSON.stringify(message), '*')
}
</script>
